import * as THREE from "three"
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js"
import { clone as cloneSkinned } from "three/addons/utils/SkeletonUtils.js"
import {
  characterFigureGlbUrl,
  characterFigureOrHumanoid,
  characterFigureShape,
  type SceneCharacterFigure,
} from "./character-figure.ts"
import { buildCharacterMannequin } from "./character-mannequin.ts"
import { createPrimitiveMesh } from "./primitive-mesh.ts"

/** Static asset path (Rails `public/`). */
export const CHARACTER_HUMANOID_URL = "/scene3d/character-humanoid.glb"

/** Roughness/metalness match scene object meshes in engine.addObject. */
const MESH_ROUGHNESS = 0.55
const MESH_METALNESS = 0.08

export type HumanoidTemplateLoader = (url: string) => Promise<THREE.Object3D>

const defaultLoader: HumanoidTemplateLoader = async (url) => (await new GLTFLoader().loadAsync(url)).scene

/**
 * One network load per URL per page, shared by every hero of that figure.
 * `template`: undefined = not loaded yet, null = load failed (stay on fallback, no retries).
 */
type TemplateCache = { promise: Promise<THREE.Object3D | null>; template: THREE.Object3D | null | undefined }
const caches = new Map<string, TemplateCache>()

function preloadByUrl(url: string, opts: { load?: HumanoidTemplateLoader } = {}): Promise<THREE.Object3D | null> {
  const existing = caches.get(url)
  if (existing) return existing.promise
  const entry: TemplateCache = { promise: Promise.resolve(null), template: undefined }
  const load = opts.load ?? defaultLoader
  entry.promise = load(url)
    .then((scene) => {
      prepareTemplate(scene)
      entry.template = scene
      return scene
    })
    .catch((err: unknown) => {
      console.warn(`[scene3d] character figure failed to load (${url}), using mannequin fallback`, err)
      entry.template = null
      return null
    })
  caches.set(url, entry)
  return entry.promise
}

export function preloadCharacterHumanoid(
  opts: { url?: string; load?: HumanoidTemplateLoader } = {},
): Promise<THREE.Object3D | null> {
  return preloadByUrl(opts.url ?? CHARACTER_HUMANOID_URL, opts)
}

/** Warm the GLBs used by these figures. Primitive figures resolve immediately. Unknown → humanoid. */
export function preloadCharacterFigures(figures: Iterable<unknown>): Promise<unknown> {
  const pending: Promise<THREE.Object3D | null>[] = []
  const seen = new Set<string>()
  for (const figure of figures) {
    const url = characterFigureGlbUrl(characterFigureOrHumanoid(figure))
    if (!url || seen.has(url)) continue
    seen.add(url)
    pending.push(preloadByUrl(url))
  }
  return Promise.all(pending)
}

/** Loaded humanoid template, `null` if loading failed, `undefined` if not requested / still loading. */
export function getCharacterHumanoidTemplate(): THREE.Object3D | null | undefined {
  return caches.get(CHARACTER_HUMANOID_URL)?.template
}

/** Tests only. */
export function resetCharacterHumanoidCache() {
  caches.clear()
}

/** Compute the model-space AABB once; every clone reuses it for normalization. */
function prepareTemplate(scene: THREE.Object3D) {
  scene.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(scene, true)
  if (box.isEmpty() || box.max.y - box.min.y <= 1e-6) throw new Error("character humanoid: empty bounding box")
  scene.userData.humanoidBox = box
}

/**
 * Per-hero instance of a shared figure mesh.
 * Local Y spans [-H/2, H/2] exactly like buildCharacterMannequin (group sits at y = H/2 in world),
 * centered on X/Z, uniformly scaled so AABB height = size[1]. No label — engine adds it.
 *
 * Geometry is cloned (not shared) because engine.removeObject disposes every mesh geometry;
 * materials are replaced with MeshStandardMaterial so the palette tint and selection highlight
 * (emissive) work the same as on primitives.
 */
export function buildCharacterHumanoid(
  template: THREE.Object3D,
  size: [number, number, number],
  color: string | number,
  rootName = "characterHumanoid",
): THREE.Group {
  const H = Math.max(size[1], 0.1)
  const box: THREE.Box3 = template.userData.humanoidBox ?? new THREE.Box3().setFromObject(template, true)
  const s = H / (box.max.y - box.min.y)

  // The clone keeps the template root's transform, which `box` already accounts for.
  const model = cloneSkinned(template)
  model.userData = {}
  model.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return
    child.geometry = child.geometry.clone()
    child.material = new THREE.MeshStandardMaterial({
      color,
      roughness: MESH_ROUGHNESS,
      metalness: MESH_METALNESS,
      flatShading: true,
    })
    child.castShadow = true
    child.receiveShadow = true
  })

  const root = new THREE.Group()
  root.name = rootName
  root.add(model)
  root.scale.setScalar(s)
  const cx = (box.min.x + box.max.x) / 2
  const cz = (box.min.z + box.max.z) / 2
  root.position.set(-cx * s, -H / 2 - box.min.y * s, -cz * s)
  return root
}

export type CharacterVisualVariant = "mannequin" | SceneCharacterFigure

function disposeTree(obj: THREE.Object3D) {
  obj.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return
    child.geometry.dispose()
    const mat = child.material
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose())
    else mat.dispose()
  })
}

function isInScene(obj: THREE.Object3D) {
  let node: THREE.Object3D | null = obj
  while (node.parent) node = node.parent
  return node instanceof THREE.Scene
}

function buildCharacterPrimitive(
  figure: SceneCharacterFigure,
  size: [number, number, number],
  color: string | number,
): THREE.Group {
  const shape = characterFigureShape(figure)
  const root = new THREE.Group()
  root.name = "characterPrimitive"
  if (!shape) return root
  root.add(createPrimitiveMesh(shape, size, color))
  return root
}

/**
 * Sync entry point for engine.addObject.
 * - Primitive figure → that shape, height from size[1], centered like other scene meshes.
 * - GLB ready → that figure immediately.
 * - Still loading → mannequin now, swapped once the shared load resolves
 *   (skipped if the hero was removed from the scene meanwhile). `onUpgrade` lets the engine
 *   re-apply selection highlight to the fresh materials.
 * - Load failed → mannequin for good; the scene keeps working.
 */
export function createCharacterVisual(
  size: [number, number, number],
  color: string | number,
  opts: { figure?: unknown; onUpgrade?: (visual: THREE.Group) => void } = {},
): THREE.Group {
  const visual = new THREE.Group()
  visual.name = "characterVisual"
  const figure = characterFigureOrHumanoid(opts.figure)
  const glbUrl = characterFigureGlbUrl(figure)

  if (!glbUrl) {
    visual.add(buildCharacterPrimitive(figure, size, color))
    visual.userData.variant = figure satisfies CharacterVisualVariant
    return visual
  }

  const mountFigure = (template: THREE.Object3D) => {
    const rootName = figure === "humanoid" ? "characterHumanoid" : "characterFigure"
    visual.add(buildCharacterHumanoid(template, size, color, rootName))
    visual.userData.variant = figure satisfies CharacterVisualVariant
  }

  const template = caches.get(glbUrl)?.template
  if (template) {
    mountFigure(template)
    return visual
  }

  visual.add(buildCharacterMannequin(size, color))
  visual.userData.variant = "mannequin" satisfies CharacterVisualVariant
  if (template === null) return visual

  void preloadByUrl(glbUrl).then((tpl) => {
    if (!tpl || !isInScene(visual)) return
    const rootName = figure === "humanoid" ? "characterHumanoid" : "characterFigure"
    let figureRoot: THREE.Group
    try {
      figureRoot = buildCharacterHumanoid(tpl, size, color, rootName)
    } catch (err) {
      console.warn(`[scene3d] character figure build failed (${figure}), keeping mannequin`, err)
      return
    }
    for (const old of visual.children.slice()) {
      visual.remove(old)
      disposeTree(old)
    }
    visual.add(figureRoot)
    visual.userData.variant = figure satisfies CharacterVisualVariant
    opts.onUpgrade?.(visual)
  })
  return visual
}
